<template>
  <div class="voucher-admin">
    <div class="header">
      <div>
        <h2>Vouchers</h2>
        <div class="subtitle">Create vouchers, bind them to recipient emails, and send notifications manually.</div>
      </div>
      <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreate">Create voucher</el-button>
    </div>

    <el-form :inline="true" size="small" class="filters" @submit.native.prevent="search">
      <el-form-item label="Status">
        <el-select v-model="filters.status" style="width: 150px" @change="search">
          <el-option label="All" value="all" />
          <el-option label="Draft" value="draft" />
          <el-option label="Issued" value="issued" />
          <el-option label="Claimed" value="claimed" />
          <el-option label="Redeemed" value="redeemed" />
          <el-option label="Voided" value="voided" />
        </el-select>
      </el-form-item>
      <el-form-item label="Type">
        <el-select v-model="filters.voucher_type" style="width: 190px" @change="search">
          <el-option label="All" value="all" />
          <el-option label="14-day subscription" value="subscription_14d" />
          <el-option label="Collection purchase" value="collection_purchase" />
          <el-option label="Collection gift" value="collection_gift" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-input
          v-model="filters.search"
          clearable
          placeholder="Email, voucher ID"
          style="width: 260px"
          @clear="search"
          @keyup.enter.native="search"
        >
          <el-button slot="append" icon="el-icon-search" @click="search" />
        </el-input>
      </el-form-item>
      <el-form-item>
        <el-button icon="el-icon-refresh" @click="fetchData">Refresh</el-button>
      </el-form-item>
    </el-form>

    <el-table v-loading="loading" :data="items" border size="small" style="width: 100%">
      <el-table-column label="Voucher" min-width="210">
        <template slot-scope="scope">
          <div class="strong">{{ scope.row.voucher_code || scope.row.id }}</div>
          <div class="muted">{{ formatType(scope.row.voucher_type) }}</div>
          <div v-if="scope.row.voucher_type === 'collection_gift'" class="muted">{{ scope.row.collection_title }}</div>
        </template>
      </el-table-column>
      <el-table-column label="Recipient" min-width="230">
        <template slot-scope="scope">
          <div>{{ scope.row.recipient_email || '-' }}</div>
          <div v-if="scope.row.claimed_by_email" class="muted">claimed by {{ scope.row.claimed_by_email }}</div>
        </template>
      </el-table-column>
      <el-table-column label="Status" width="130">
        <template slot-scope="scope">
          <el-tag :type="statusTag(scope.row.status)" size="small">{{ formatStatus(scope.row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Notification" width="170">
        <template slot-scope="scope">
          <div>{{ scope.row.notification_sent_at ? 'Sent' : 'Not sent' }}</div>
          <div class="muted">{{ formatDate(scope.row.notification_sent_at) }}</div>
        </template>
      </el-table-column>
      <el-table-column label="Created" width="150">
        <template slot-scope="scope">{{ formatDate(scope.row.created_at || scope.row.create_time) }}</template>
      </el-table-column>
      <el-table-column label="Actions" width="170" fixed="right">
        <template slot-scope="scope">
          <el-button
            size="mini"
            type="primary"
            :disabled="!canSend(scope.row)"
            :loading="sendingId === rowId(scope.row)"
            @click="sendVoucher(scope.row)"
          >
            Send
          </el-button>
          <el-button v-if="scope.row.voucher_type === 'collection_gift' && ['draft', 'issued'].includes(scope.row.status)" size="mini" type="danger" plain @click="voidGift(scope.row)">Void</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next"
        :current-page.sync="page"
        :page-size.sync="pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        @size-change="fetchData"
        @current-change="fetchData"
      />
    </div>

    <el-dialog title="Create Voucher" :visible.sync="createVisible" width="560px" :close-on-click-modal="false">
      <el-form ref="createForm" :model="createForm" :rules="rules" label-width="140px" size="small">
        <el-form-item label="Recipient email" prop="recipient_email">
          <el-input v-model.trim="createForm.recipient_email" type="email" placeholder="recipient@example.com" />
        </el-form-item>
        <el-form-item label="Voucher type" prop="voucher_type">
          <el-radio-group v-model="createForm.voucher_type">
            <el-radio label="subscription_14d">14-day subscription</el-radio>
            <el-radio label="collection_purchase">Collection purchase</el-radio>
            <el-radio label="collection_gift">Gift a collection</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="createForm.voucher_type === 'collection_gift'" label="Collection" prop="collection_id">
          <el-select v-model="createForm.collection_id" filterable remote :remote-method="searchGiftCollections" placeholder="Search published collections" style="width:100%" @focus="searchGiftCollections('')">
            <el-option v-for="item in giftCollections" :key="collectionId(item)" :label="item.title" :value="collectionId(item)" />
          </el-select>
        </el-form-item>
        <el-form-item label="Admin note">
          <el-input
            v-model="createForm.admin_note"
            type="textarea"
            :rows="3"
            maxlength="1000"
            show-word-limit
            placeholder="Optional internal note."
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="createVisible = false">Cancel</el-button>
        <el-button type="primary" :loading="creating" @click="createVoucher">Create draft</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import {
  createVoucherForAdmin,
  listVouchersForAdmin,
  sendVoucherForAdmin,
  searchCollectionsForAdmin,
  postRequest
} from '@/apis/lfAdmin'

export default {
  name: 'LFAdminVouchers',
  data() {
    return {
      loading: false,
      creating: false,
      sendingId: '',
      createVisible: false,
      giftCollections: [],
      items: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filters: {
        status: 'all',
        voucher_type: 'all',
        search: ''
      },
      createForm: {
        recipient_email: '',
        voucher_type: 'subscription_14d',
        collection_id: '',
        admin_note: ''
      },
      rules: {
        recipient_email: [
          { required: true, message: 'Enter a recipient email.', trigger: 'blur' },
          { type: 'email', message: 'Enter a valid email.', trigger: 'blur' }
        ],
        voucher_type: [
          { required: true, message: 'Choose a voucher type.', trigger: 'change' }
        ],
        collection_id: [
          { required: true, message: 'Choose a published collection.', trigger: 'change' }
        ]
      }
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    search() {
      this.page = 1
      this.fetchData()
    },
    buildParams() {
      const params = {
        page: this.page,
        page_size: this.pageSize
      }
      if (this.filters.status !== 'all') params.status = this.filters.status
      if (this.filters.voucher_type !== 'all') params.voucher_type = this.filters.voucher_type
      if (this.filters.search.trim()) params.search = this.filters.search.trim()
      return params
    },
    async fetchData() {
      this.loading = true
      try {
        const res = await listVouchersForAdmin(this.buildParams())
        if (res.data.code === 0) {
          const data = res.data.data || {}
          this.items = data.items || []
          this.total = Number(data.total || 0)
        } else {
          this.$message.error(res.data.msg || 'Failed to load vouchers')
        }
      } catch (e) {
        this.$message.error('Failed to load vouchers')
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.createForm = {
        recipient_email: '',
        voucher_type: 'subscription_14d',
        collection_id: '',
        admin_note: ''
      }
      this.createVisible = true
      this.$nextTick(() => {
        if (this.$refs.createForm) this.$refs.createForm.clearValidate()
      })
    },
    createVoucher() {
      this.$refs.createForm.validate(async (valid) => {
        if (!valid) return
        this.creating = true
        try {
          const res = await createVoucherForAdmin(this.createForm)
          if (res.data.code === 0) {
            this.$message.success('Voucher draft created')
            this.createVisible = false
            this.fetchData()
          } else {
            this.$message.error(res.data.msg || 'Failed to create voucher')
          }
        } catch (e) {
          this.$message.error('Failed to create voucher')
        } finally {
          this.creating = false
        }
      })
    },
    async sendVoucher(row) {
      const id = this.rowId(row)
      if (!id) return
      this.sendingId = id
      try {
        const res = await sendVoucherForAdmin({ voucher_id: id })
        if (res.data.code === 0) {
          this.$message.success('Voucher notification sent')
          this.fetchData()
        } else {
          this.$message.error(res.data.msg || 'Failed to send voucher')
        }
      } catch (e) {
        this.$message.error('Failed to send voucher')
      } finally {
        this.sendingId = ''
      }
    },
    collectionId(item) {
      const id = item && item._id
      return typeof id === 'string' ? id : ((id && (id.$oid || id.$id)) || '')
    },
    async searchGiftCollections(query) {
      try {
        const res = await searchCollectionsForAdmin({ q: query, limit: 30 })
        if (res.data.code === 0) this.giftCollections = res.data.data || []
      } catch (e) { this.giftCollections = [] }
    },
    async voidGift(row) {
      const id = this.rowId(row)
      if (!id) return
      try {
        await this.$confirm('Void this unclaimed collection gift?', 'Void gift', { type: 'warning' })
        const res = await postRequest('adminVoidCollectionGift', { voucher_id: id })
        if (res.data.code === 0) { this.$message.success('Gift voided'); this.fetchData() }
        else this.$message.error(res.data.msg || 'Could not void gift')
      } catch (e) { /* cancelled */ }
    },
    canSend(row) {
      return row && ['draft', 'issued'].includes(row.status)
    },
    rowId(row) {
      if (!row) return ''
      if (row.id) return row.id
      const id = row._id
      if (!id) return ''
      if (typeof id === 'string') return id
      return id.$oid || id.$id || ''
    },
    formatType(type) {
      const map = {
        subscription_14d: '14-day subscription',
        collection_purchase: 'Collection purchase',
        collection_gift: 'Collection gift'
      }
      return map[type] || type || '-'
    },
    formatStatus(status) {
      const map = {
        draft: 'Draft',
        issued: 'Issued',
        claimed: 'Claimed',
        redeemed: 'Redeemed',
        voided: 'Voided'
      }
      return map[status] || status || '-'
    },
    statusTag(status) {
      if (status === 'redeemed') return 'success'
      if (status === 'claimed') return 'warning'
      if (status === 'voided') return 'info'
      return ''
    },
    formatDate(ts) {
      const value = Number(ts || 0)
      if (!value) return '-'
      return dayjs.unix(value).format('YYYY-MM-DD HH:mm')
    }
  }
}
</script>

<style scoped>
.voucher-admin {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.header h2 {
  margin: 0;
  color: #303133;
  font-size: 22px;
  line-height: 1.25;
}

.subtitle,
.muted {
  color: #909399;
  font-size: 12px;
}

.subtitle {
  margin-top: 4px;
  font-size: 13px;
}

.filters {
  padding: 12px 12px 0;
  margin-bottom: 14px;
  background: #fff;
  border: 1px solid #ebeef5;
}

.strong {
  color: #303133;
  font-weight: 600;
}

.pagination {
  margin-top: 16px;
  text-align: right;
}
</style>
